import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { API } from "@/lib/api";
import { Cookie, X } from "lucide-react";

const KEY = "findo_cookie_consent";
export const OPEN_COOKIES_EVENT = "findloop:open-cookies";

function readConsent() {
  try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
}

export default function CookieBanner() {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [measurement, setMeasurement] = useState(true);

  useEffect(() => {
    if (!readConsent()) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      const c = readConsent();
      setMeasurement(c ? !!c.measurement : true);
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIES_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIES_EVENT, open);
  }, []);

  function save(consent) {
    localStorage.setItem(KEY, JSON.stringify({ ...consent, ts: new Date().toISOString() }));
    setVisible(false);
    setCustomize(false);
    toast.success(t("ck.saved"));
    // Persist a timestamped consent event for the RGPD journal (best-effort).
    fetch(`${API}/gdpr/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        purpose: "cookies",
        granted: !!consent.measurement,
        meta: { necessary: true, measurement: !!consent.measurement, lang },
      }),
    }).catch(() => {});
  }

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-40 animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="cookie-banner">
      <div className="bg-[#0E0E12] text-white rounded-2xl shadow-2xl border border-white/10 p-5">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-findo-red/15 text-findo-red">
            <Cookie size={18} />
          </span>
          <div className="flex-1 min-w-0">
            {!customize ? (
              <>
                <p className="text-sm text-white/80 leading-relaxed">
                  {t("ck.msg")}{" "}
                  <Link to="/cookies" className="underline font-semibold hover:text-white" data-testid="cookie-learn">
                    {t("ck.learn")}
                  </Link>
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => save({ necessary: true, measurement: true })}
                    className="flex-1 rounded-full bg-findo-red hover:bg-findo-red/90 text-white text-sm font-bold py-2.5 transition-colors"
                    data-testid="cookie-accept">
                    {t("ck.acceptAll")}
                  </button>
                  <button
                    onClick={() => save({ necessary: true, measurement: false })}
                    className="flex-1 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold py-2.5 transition-colors"
                    data-testid="cookie-refuse">
                    {t("ck.refuse")}
                  </button>
                </div>
                <button
                  onClick={() => setCustomize(true)}
                  className="mt-3 text-xs text-white/50 hover:text-white underline transition-colors"
                  data-testid="cookie-customize">
                  {t("ck.customize")}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-bold">{t("ck.title")}</p>
                <div className="mt-3 space-y-3">
                  <ConsentRow
                    title={t("ck.necessary")} desc={t("ck.necessaryDesc")}
                    checked disabled testid="cookie-necessary" />
                  <ConsentRow
                    title={t("ck.measurement")} desc={t("ck.measurementDesc")}
                    checked={measurement} onChange={setMeasurement} testid="cookie-measurement" />
                </div>
                <Link to="/confidentialite" className="mt-3 inline-block text-xs text-white/50 hover:text-white underline transition-colors" data-testid="cookie-privacy-link">
                  {t("ck.learn")}
                </Link>
                <button
                  onClick={() => save({ necessary: true, measurement })}
                  className="mt-4 w-full rounded-full bg-findo-red hover:bg-findo-red/90 text-white text-sm font-bold py-2.5 transition-colors"
                  data-testid="cookie-save">
                  {t("ck.save")}
                </button>
              </>
            )}
          </div>
          <button onClick={() => save({ necessary: true, measurement: false })} className="text-white/40 hover:text-white transition-colors" aria-label="close" data-testid="cookie-close">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange && onChange(!checked)}
        className={`mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors relative ${checked ? "bg-findo-red" : "bg-white/20"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={testid}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
      </button>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white/90">{title}</p>
        <p className="text-xs text-white/50 leading-snug">{desc}</p>
      </div>
    </div>
  );
}
